/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Link,
} from "@farmlands/shadcn-ui";
import { ErrorLayout } from "@farmlands/ui-library";
import { FC, JSX } from "react";
import { useSelector } from "react-redux";

import { selectRemoteConfig } from "../../../../../services/firebase/remote-config/remoteConfig.slice";
import TestIds from "../../../../../testIds";

export interface IDeleteAccountErrorProps {
  open: boolean;
  onRetry: () => void;
  onClose: () => void;
}

const DeleteAccountError: FC<IDeleteAccountErrorProps> = ({
  open,
  onRetry,
  onClose,
}): JSX.Element => {
  const { contactUrl } = useSelector(selectRemoteConfig);

  const handleOpenChange = (isOpen: boolean): void => {
    if (!isOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-125!"
        data-testid={TestIds.feat.account.profile.deleteAccountModal.error.component}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Delete account request error</DialogTitle>

        <DialogDescription className="sr-only">
          An error occurred while processing your request
        </DialogDescription>

        <DialogBody>
          <ErrorLayout
            size="large"
            className="px-0! pt-10! pb-0!"
            heading={<h2 className="text-lg font-bold">Something went wrong</h2>}
            description={
              <>
                <p>
                  We're having trouble completing your request. <br className="hidden lg:block" />
                  Please try again, and if the issue persists,{" "}
                  <Link
                    href={contactUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm leading-md text-forest-green-800"
                  >
                    contact us
                  </Link>{" "}
                  for assistance.
                </p>
              </>
            }
          >
            <Button
              onClick={onRetry}
              data-testid={TestIds.feat.account.profile.deleteAccountModal.error.tryAgainBtn}
            >
              Try Again
            </Button>
          </ErrorLayout>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountError;
